import React, { useState, useEffect } from "react";
import PlacesAutocomplete from "react-places-autocomplete";

const PlaceComponent = ({ value, onChange, field, label, error }) => {
  const [address, setAddress] = useState(value || "");

  const formatAddressWithZip = async (placeId) => {
    try {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.warn('Google Maps API not available');
        return null;
      }
      
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      return new Promise((resolve, reject) => {
        service.getDetails({ placeId, fields: ['address_components', 'formatted_address'] }, (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            const components = place.address_components;
            const streetNumber = components.find(c => c.types.includes('street_number'))?.long_name || '';
            const route = components.find(c => c.types.includes('route'))?.long_name || '';
            const city = components.find(c => c.types.includes('locality'))?.long_name || '';
            const state = components.find(c => c.types.includes('administrative_area_level_1'))?.short_name || '';
            const zipCode = components.find(c => c.types.includes('postal_code'))?.long_name || '';
            
            // Format: "Street, City, State ZIP"
            let formattedAddress = '';
            if (streetNumber && route) {
              formattedAddress = `${streetNumber} ${route}`;
            } else if (route) {
              formattedAddress = route;
            } else {
              // Fallback to formatted address if components aren't available
              formattedAddress = place.formatted_address.split(',')[0];
            }
            
            if (city && state) {
              if (zipCode) {
                formattedAddress = `${formattedAddress}, ${city}, ${state} ${zipCode}`;
              } else {
                formattedAddress = `${formattedAddress}, ${city}, ${state}`;
              }
            }
            
            resolve(formattedAddress || place.formatted_address);
          } else {
            reject(new Error('Failed to get place details'));
          }
        });
      });
    } catch (error) {
      console.error('Error formatting address:', error);
      return null;
    }
  };

  const handleSelect = async (address, placeId, suggestion) => {
    setAddress(address);
    
    // If we have a placeId, get detailed address with zip code
    const selectedPlaceId = placeId || suggestion?.placeId;
    if (selectedPlaceId) {
      try {
        const formattedAddress = await formatAddressWithZip(selectedPlaceId);
        if (formattedAddress) {
          setAddress(formattedAddress);
          onChange(field, formattedAddress);
          return;
        }
      } catch (error) {
        console.error('Error formatting address:', error);
      }
    }
    
    // Fallback: remove "USA" and try to format manually
    let formattedAddress = address;
    if (address.includes(', USA')) {
      formattedAddress = address.replace(/, USA$/, '').trim();
    }
    
    onChange(field, formattedAddress);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const input = document.querySelector(".pac-target-input");
      if (input) {
        input.setAttribute("placeholder", "");
  
        const observer = new MutationObserver(() => {
          input.setAttribute("placeholder", "");
        });
        observer.observe(input, {
          attributes: true,
          attributeFilter: ["placeholder"],
        });
  
        clearInterval(interval);
      }
    }, 100);
  
    return () => clearInterval(interval);
  }, []);
  

  return (
    <div className="place-wrapper">
      <label className="form-label">{label}<span className="text-danger">*</span></label>
      <PlacesAutocomplete
        value={address}
        onChange={setAddress}
        onSelect={handleSelect}
        debounce={300}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <div className="autocomplete-root">
            <input
              {...getInputProps({
                placeholder: "",
                className: "form-control",
              })}
            />

            {(loading || suggestions.length > 0) && (
                <div className="autocomplete-dropdown">
                {loading && <div className="suggestion-item">Loading...</div>}

                {suggestions.map((suggestion) => {
                    const className = suggestion.active
                    ? "suggestion-item active"
                    : "suggestion-item";
                    return (
                    <div
                        {...getSuggestionItemProps(suggestion, {
                        className,
                        })}
                        key={suggestion.placeId}
                    >
                        {suggestion.description}
                    </div>
                    );
                })}
                </div>
             )}     
            {error && <div className="text-danger mt-1">{error}</div>}
          </div>
        )}
      </PlacesAutocomplete>
    </div>
  );
};

export default PlaceComponent;
